(() => {
  const initTabs = (root) => {
    root.querySelectorAll('[data-rm-tabs]').forEach((group) => {
      if (group.dataset.rmTabsReady) return;
      group.dataset.rmTabsReady = 'true';
      const tabs = [...group.querySelectorAll('[role="tab"]')];
      const select = (tab, focus) => {
        tabs.forEach((item) => {
          const active = item === tab;
          item.setAttribute('aria-selected', active ? 'true' : 'false');
          item.tabIndex = active ? 0 : -1;
          const panel = group.querySelector(`#${item.getAttribute('aria-controls')}`);
          if (panel) panel.hidden = !active;
        });
        group.querySelectorAll('[data-rm-tabs-all]').forEach((link) => {
          if (tab.dataset.rmUrl) link.href = tab.dataset.rmUrl;
        });
        if (focus) tab.focus();
        const panel = group.querySelector(`#${tab.getAttribute('aria-controls')}`);
        panel?.dispatchEvent(new CustomEvent('rm:rail-refresh'));
      };
      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => select(tab, false));
        tab.addEventListener('keydown', (event) => {
          let next = null;
          if (event.key === 'ArrowRight') next = tabs[(index + 1) % tabs.length];
          if (event.key === 'ArrowLeft') next = tabs[(index - 1 + tabs.length) % tabs.length];
          if (event.key === 'Home') next = tabs[0];
          if (event.key === 'End') next = tabs[tabs.length - 1];
          if (!next) return;
          event.preventDefault();
          select(next, true);
        });
      });
    });
  };

  const initRails = (root) => {
    root.querySelectorAll('[data-rm-rail]').forEach((rail) => {
      if (rail.dataset.rmRailReady) return;
      const track = rail.querySelector('[data-rm-rail-track]');
      if (!track) return;
      rail.dataset.rmRailReady = 'true';
      const prev = rail.querySelector('[data-rm-rail-prev]');
      const next = rail.querySelector('[data-rm-rail-next]');
      const bar = rail.querySelector('[data-rm-rail-bar]');

      const update = () => {
        const max = track.scrollWidth - track.clientWidth;
        const overflow = max > 1;
        const ratio = overflow ? track.clientWidth / track.scrollWidth : 1;
        const progress = overflow ? track.scrollLeft / max : 0;
        if (bar) {
          bar.style.setProperty('--rm-bar-w', `${ratio * 100}%`);
          bar.style.setProperty('--rm-bar-x', `${(progress * (1 - ratio) / ratio) * 100}%`);
          bar.parentElement.hidden = !overflow;
        }
        if (prev) {
          prev.hidden = !overflow;
          prev.disabled = !overflow || track.scrollLeft <= 1;
          prev.setAttribute('aria-disabled', prev.disabled ? 'true' : 'false');
        }
        if (next) {
          next.hidden = !overflow;
          next.disabled = !overflow || track.scrollLeft >= max - 1;
          next.setAttribute('aria-disabled', next.disabled ? 'true' : 'false');
        }
      };

      const step = (dir) => {
        const items = [...track.children];
        if (!items.length) return;

        const origin = items[0].offsetLeft;
        const current = items.reduce((closest, item, index) => {
          const distance = Math.abs(item.offsetLeft - origin - track.scrollLeft);
          return distance < closest.distance ? { index, distance } : closest;
        }, { index: 0, distance: Infinity });
        const targetIndex = Math.min(items.length - 1, Math.max(0, current.index + dir));

        track.scrollTo({
          left: items[targetIndex].offsetLeft - origin,
          behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
        });
      };

      prev?.addEventListener('click', () => { if (prev.getAttribute('aria-disabled') !== 'true') step(-1); });
      next?.addEventListener('click', () => { if (next.getAttribute('aria-disabled') !== 'true') step(1); });
      track.addEventListener('scroll', () => requestAnimationFrame(update), { passive: true });
      rail.addEventListener('rm:rail-refresh', () => requestAnimationFrame(update));
      if ('ResizeObserver' in window) new ResizeObserver(update).observe(track);
      update();
    });
  };

  const initStory = (root) => {
    root.querySelectorAll('[data-rm-story]').forEach((list) => {
      if (list.dataset.rmStoryReady) return;
      list.dataset.rmStoryReady = 'true';
      const section = list.closest('.rm-story');
      const buttons = [...list.querySelectorAll('button.rm-story__toggle')];
      const controller = new AbortController();
      const options = { signal: controller.signal };
      const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      let manual = false;
      let frame = 0;
      const activate = (toggle) => {
        list.querySelectorAll('.rm-story__item').forEach((item) => {
          const button = item.querySelector('button.rm-story__toggle');
          const open = button === toggle;
          item.classList.toggle('is-active', open);
          if (!button) return;
          button.setAttribute('aria-expanded', open ? 'true' : 'false');
          const panel = document.getElementById(button.getAttribute('aria-controls'));
          if (panel) panel.hidden = !open;
        });
      };
      const update = () => {
        frame = 0;
        if (reducedMotion.matches || !buttons.length) return;
        const rect = section.getBoundingClientRect();
        if (rect.bottom <= 0 || rect.top >= innerHeight) {
          manual = false;
          return;
        }
        if (manual || section.contains(document.activeElement)) return;
        const progress = (innerHeight * 0.75 - rect.top) / (rect.height + innerHeight * 0.25);
        const index = Math.min(buttons.length - 1, Math.max(0, Math.floor(progress * buttons.length)));
        activate(buttons[index]);
      };
      const schedule = () => {
        if (!frame) frame = requestAnimationFrame(update);
      };
      list.addEventListener('click', (event) => {
        const toggle = event.target.closest('button.rm-story__toggle');
        if (!toggle) return;
        manual = true;
        activate(toggle);
      }, options);
      window.addEventListener('scroll', schedule, { ...options, passive: true });
      window.addEventListener('resize', schedule, options);
      reducedMotion.addEventListener('change', schedule, options);
      document.addEventListener('shopify:section:unload', (event) => {
        if (!event.target.contains(list)) return;
        controller.abort();
        cancelAnimationFrame(frame);
      }, options);
      schedule();
    });
  };

  const init = (root = document) => {
    initTabs(root);
    initRails(root);
    initStory(root);
  };

  init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
